import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Loader2,
  RefreshCw,
  ExternalLink,
  Building2,
  Mail,
  Phone,
  CheckCircle2,
  AlertTriangle,
  Clock,
  TrendingUp,
  DollarSign,
  Copy,
  CheckCheck,
  AlertCircle,
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso, withTime = false) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return withTime
      ? d.toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
      : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
};

const StatusBadge = ({ acct }) => {
  if (acct?.disabled_reason) {
    return (
      <Badge className="bg-red-500/15 text-red-400 border-red-500/30 gap-1">
        <AlertTriangle className="w-3 h-3" /> Restricted
      </Badge>
    );
  }
  if (acct?.onboarding_complete) {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30 gap-1">
        <CheckCircle2 className="w-3 h-3" /> Active
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/30 gap-1">
      <Clock className="w-3 h-3" /> Pending
    </Badge>
  );
};

const FeeModelPill = ({ feeModel, testId }) => (
  <span
    className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md border whitespace-nowrap ${
      feeModel === "merchant_pays"
        ? "border-emerald-500/30 text-emerald-500 bg-emerald-500/5"
        : "border-amber-500/30 text-amber-500 bg-amber-500/5"
    }`}
    data-testid={testId}
  >
    {feeModel === "merchant_pays" ? "1% Direct" : "Legacy 3.8%"}
  </span>
);

const TxStatusPill = ({ status }) => {
  const map = {
    paid: { c: "text-emerald-500 bg-emerald-500/10 border-emerald-500/30", l: "Paid" },
    pending: { c: "text-amber-500 bg-amber-500/10 border-amber-500/30", l: "Pending" },
    initiated: { c: "text-amber-500 bg-amber-500/10 border-amber-500/30", l: "Initiated" },
    refunded: { c: "text-blue-400 bg-blue-500/10 border-blue-500/30", l: "Refunded" },
    disputed: { c: "text-red-400 bg-red-500/10 border-red-500/30", l: "Disputed" },
    failed: { c: "text-red-400 bg-red-500/10 border-red-500/30", l: "Failed" },
  };
  const s = map[status] || { c: "text-muted-foreground bg-muted/30 border-border", l: status || "—" };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${s.c}`}>
      {s.l}
    </span>
  );
};

const Metric = ({ label, value, accent }) => (
  <div className="space-y-0.5">
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{label}</p>
    <p className={`text-base font-semibold ${accent || "text-foreground"}`}>{value}</p>
  </div>
);

const CopyableId = ({ value, testId }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-muted-foreground text-xs">—</span>;
  const onCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(value).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1500); },
      () => toast.error("Copy failed"),
    );
  };
  return (
    <button
      onClick={onCopy}
      className="font-mono text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 group"
      title="Copy"
      data-testid={testId}
    >
      <span className="truncate max-w-[180px]">{value}</span>
      {copied ? <CheckCheck className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3 opacity-50 group-hover:opacity-100" />}
    </button>
  );
};

const AdminStripeConnectDetailModal = ({ userId, open, onOpenChange, onAfterResync }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [expandedTx, setExpandedTx] = useState(null);
  const [ovPercent, setOvPercent] = useState("");
  const [ovFlat, setOvFlat] = useState("");
  const [ovSaving, setOvSaving] = useState(false);

  const getAuth = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  const fetchDetail = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/stripe-connect/accounts/${userId}`, getAuth());
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load merchant detail");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open && userId) fetchDetail();
    if (!open) setData(null);
  }, [open, userId, fetchDetail]);

  const onResync = async () => {
    setResyncing(true);
    try {
      const res = await axios.post(`${API}/admin/stripe-connect/accounts/${userId}/resync`, {}, getAuth());
      toast.success(`Synced — status: ${res.data.status}`);
      await fetchDetail();
      onAfterResync?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resync failed");
    } finally {
      setResyncing(false);
    }
  };

  const user = data?.user;
  const acct = data?.stripe_account;
  const m = data?.metrics;
  const txs = data?.recent_transactions || [];

  const saveOverride = async (clear = false) => {
    setOvSaving(true);
    try {
      const body = clear
        ? { clear: true }
        : { percent: parseFloat(ovPercent), flat_cents: Math.round((parseFloat(ovFlat) || 0) * 100) };
      if (!clear && (isNaN(body.percent) || body.percent < 0 || body.percent > 20)) {
        toast.error("Percent must be 0–20"); setOvSaving(false); return;
      }
      const res = await axios.put(`${API}/admin/platform-fee/override/${userId}`, body, getAuth());
      toast.success(clear ? "Override cleared — platform default applies" : "Merchant fee override saved");
      setData((d) => ({ ...d, fee: res.data.effective_fee, fee_override: clear ? null : { percent: body.percent, flat_cents: body.flat_cents } }));
      setOvPercent(""); setOvFlat("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update override");
    } finally {
      setOvSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="admin-stripe-connect-detail-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2 pr-8">
            <Building2 className="w-5 h-5 text-[#635BFF]" />
            <span className="truncate">
              {user?.business_name || user?.name || "Merchant"} · Stripe Connect
            </span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : !data ? (
          <div className="py-20 text-center text-muted-foreground">
            Detail unavailable.
          </div>
        ) : (
          <div className="overflow-y-auto flex-1 pr-1 space-y-5">
            {/* Header — identity + status + dashboard link + resync */}
            <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="space-y-1 min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">
                    {user?.business_name || user?.name || "—"}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    {user?.email && (
                      <span className="flex items-center gap-1 truncate">
                        <Mail className="w-3 h-3" /> {user.email}
                      </span>
                    )}
                    {user?.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {user.phone}
                      </span>
                    )}
                    {user?.subscription_plan && (
                      <Badge variant="outline" className="text-[10px] uppercase">
                        {user.subscription_plan}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Account</span>
                    <CopyableId value={acct?.account_id} testId="admin-detail-copy-acct-id" />
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="flex items-center gap-2">
                    <FeeModelPill feeModel={acct?.fee_model} testId="admin-detail-fee-model" />
                    <StatusBadge acct={acct} />
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Last synced {fmtDate(acct?.last_synced, true)}
                  </p>
                  {acct?.fee_model === "merchant_pays" && acct?.migrated_at && (
                    <p className="text-[10px] text-emerald-500" data-testid="admin-detail-migrated-at">
                      Switched to 1% direct {fmtDate(acct.migrated_at)}
                    </p>
                  )}
                </div>
              </div>

              {acct?.disabled_reason && (
                <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/5 p-2">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-red-400">
                    <span className="font-medium">Disabled reason: </span>
                    {acct.disabled_reason}
                  </p>
                </div>
              )}

              {/* Capabilities */}
              <div className="flex flex-wrap gap-2 text-[11px]">
                <Cap label="Charges" enabled={acct?.charges_enabled} testId="admin-detail-cap-charges" />
                <Cap label="Payouts" enabled={acct?.payouts_enabled} testId="admin-detail-cap-payouts" />
                <Cap label="Details Submitted" enabled={acct?.details_submitted} />
                <Cap label="Onboarding Complete" enabled={acct?.onboarding_complete} />
              </div>

              {/* Action row */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-border/40">
                {acct?.dashboard_url && (
                  <a href={acct.dashboard_url} target="_blank" rel="noopener noreferrer">
                    <Button
                      size="sm"
                      className="gap-2 bg-[#635BFF] hover:bg-[#7367ff] text-white"
                      data-testid="admin-detail-dashboard-link"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> View in Stripe Dashboard
                    </Button>
                  </a>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onResync}
                  disabled={resyncing}
                  className="gap-2"
                  data-testid="admin-detail-resync-btn"
                >
                  {resyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  Resync from Stripe
                </Button>
              </div>
            </div>

            {/* Metrics — 30d / 90d / lifetime grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <PeriodCard
                title="Last 30 days"
                volume={m?.volume_30d}
                fees={m?.fees_30d}
                count={m?.transactions_30d}
              />
              <PeriodCard
                title="Last 90 days"
                volume={m?.volume_90d}
                fees={m?.fees_90d}
                count={m?.transactions_90d}
              />
              <PeriodCard
                title="Lifetime"
                volume={m?.lifetime_volume}
                fees={m?.lifetime_fees}
                count={m?.lifetime_transactions}
                accent
              />
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2" data-testid="admin-detail-net-revenue-card">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Your Net Revenue</p>
                <Metric label="Net to you (lifetime)" value={fmtMoney(m?.lifetime_net_revenue)} accent="text-emerald-500" />
                {(m?.lifetime_stripe_cost || 0) > 0 && (
                  <Metric
                    label={`Stripe costs (legacy${m?.cost_estimated_txns ? `, ~${m.cost_estimated_txns} est.` : ""})`}
                    value={`−${fmtMoney(m.lifetime_stripe_cost)}`}
                    accent="text-red-400"
                  />
                )}
                <Metric label="Avg ticket" value={fmtMoney(m?.avg_ticket)} />
                <Metric
                  label="Dispute rate"
                  value={`${m?.dispute_rate_pct ?? 0}%`}
                  accent={(m?.dispute_rate_pct ?? 0) > 1 ? "text-red-400" : "text-foreground"}
                />
              </div>
            </div>

            {/* Platform fee for this merchant (super-admin override) */}
            <div className="rounded-xl border border-border bg-muted/20 p-3 space-y-2" data-testid="merchant-fee-section">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Platform Fee (this merchant)</p>
                  <p className="text-sm font-semibold text-foreground" data-testid="merchant-effective-fee">
                    {data?.fee ? `${data.fee.percent}% + ${fmtMoney((data.fee.flat_cents || 0) / 100)}` : "—"}
                    <span className="text-[10px] font-normal text-muted-foreground ml-2">
                      {data?.fee?.source === "merchant_override" ? "custom override" : "platform default"}
                      {" · "}
                      {acct?.fee_model === "merchant_pays" ? "direct structure (pure revenue)" : "legacy structure (platform pays Stripe)"}
                    </span>
                  </p>
                </div>
                <div className="flex items-end gap-2">
                  <div>
                    <label className="text-[10px] text-muted-foreground block mb-0.5">Override %</label>
                    <input type="number" min="0" max="20" step="0.1" value={ovPercent} onChange={(e) => setOvPercent(e.target.value)}
                      className="w-20 h-7 rounded-md border border-border bg-background px-2 text-xs" data-testid="merchant-override-percent" />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground block mb-0.5">Flat $</label>
                    <input type="number" min="0" max="10" step="0.01" value={ovFlat} onChange={(e) => setOvFlat(e.target.value)}
                      className="w-20 h-7 rounded-md border border-border bg-background px-2 text-xs" data-testid="merchant-override-flat" />
                  </div>
                  <Button size="sm" className="h-7 text-xs" disabled={ovSaving || ovPercent === ""} onClick={() => saveOverride(false)} data-testid="merchant-override-save">
                    Apply
                  </Button>
                  {data?.fee?.source === "merchant_override" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={ovSaving} onClick={() => saveOverride(true)} data-testid="merchant-override-clear">
                      Reset to default
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Recent transactions */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-foreground">Recent Connect Transactions</h3>
                <span className="text-xs text-muted-foreground">
                  {txs.length} shown
                </span>
              </div>
              {txs.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border py-8 text-center text-sm text-muted-foreground" data-testid="admin-detail-no-txs">
                  No Connect payments yet for this merchant.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/30">
                      <tr className="text-left text-muted-foreground">
                        <th className="font-medium px-3 py-2">Date</th>
                        <th className="font-medium px-3 py-2">Invoice</th>
                        <th className="font-medium px-3 py-2">Customer</th>
                        <th className="font-medium px-3 py-2 text-right">Amount</th>
                        <th className="font-medium px-3 py-2 text-right">Fee</th>
                        <th className="font-medium px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {txs.map((t) => {
                        const key = t.transaction_id || t.session_id;
                        const isOpen = expandedTx === key;
                        return (
                          <React.Fragment key={key}>
                            <tr
                              className="border-t border-border/40 hover:bg-muted/20 cursor-pointer"
                              onClick={() => setExpandedTx(isOpen ? null : key)}
                              data-testid={`admin-detail-tx-row-${key}`}
                            >
                              <td className="px-3 py-2 text-muted-foreground">
                                {fmtDate(t.created_at)}
                              </td>
                              <td className="px-3 py-2 font-mono">
                                {t.invoice_number || (t.invoice_id ? t.invoice_id.slice(0, 10) : "—")}
                              </td>
                              <td className="px-3 py-2 truncate max-w-[160px]">
                                {t.customer_name || "—"}
                              </td>
                              <td className="px-3 py-2 text-right font-mono">{fmtMoney(t.amount)}</td>
                              <td className="px-3 py-2 text-right font-mono text-[#FFCC00]">
                                {fmtMoney(t.application_fee)}
                                <span className={`block text-[9px] uppercase tracking-wider font-sans ${t.charge_type === "direct" ? "text-emerald-500/70" : "text-amber-500/70"}`}>
                                  {t.charge_type === "direct" ? "direct" : "legacy"}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <TxStatusPill status={t.payment_status} />
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="border-t border-border/20 bg-muted/10" data-testid={`admin-detail-tx-expanded-${key}`}>
                                <td colSpan={6} className="px-4 py-3">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1.5 text-[11px]">
                                    <FeeLine label="Gross customer payment" value={fmtMoney(t.amount)} />
                                    <FeeLine label={`Percentage fee${t.platform_fee_percent != null ? ` (${t.platform_fee_percent}%)` : ""}`} value={fmtMoney(t.platform_fee_pct_amount)} />
                                    <FeeLine label="Flat fee" value={fmtMoney(t.platform_fee_flat_amount)} />
                                    <FeeLine label="Total Fieldz AI fee" value={fmtMoney(t.application_fee)} accent />
                                    <FeeLine
                                      label="Charge type"
                                      value={t.charge_type === "direct"
                                        ? `Direct — fee is pure revenue${t.legacy_fallback ? " (legacy fallback)" : ""}`
                                        : `Destination (legacy) — platform pays Stripe${t.legacy_fallback ? ", via fallback account" : ""}`}
                                    />
                                    <FeeLine
                                      label={t.charge_type === "direct" ? "Stripe fee (paid by merchant)" : "Stripe fee (paid by platform)"}
                                      value={t.stripe_processing_fee != null ? fmtMoney(t.stripe_processing_fee) : "n/a"}
                                    />
                                    <FeeLine label="Net to merchant" value={t.net_to_merchant != null ? fmtMoney(t.net_to_merchant) : fmtMoney((t.amount || 0) - (t.application_fee || 0))} />
                                    <FeeLine label="Tip included" value={t.tip_amount ? fmtMoney(t.tip_amount) : "—"} />
                                    <FeeLine label="Payment kind" value={t.payment_kind || "full"} />
                                    {t.refunded_amount != null && (
                                      <>
                                        <FeeLine label="Refunded" value={fmtMoney(t.refunded_amount)} />
                                        <FeeLine label="Fee refunded" value={fmtMoney(t.platform_fee_refunded || 0)} />
                                        <FeeLine label="Fee kept" value={fmtMoney(t.platform_fee_kept)} accent />
                                      </>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 pt-2 border-t border-border/30 text-[10px] text-muted-foreground">
                                    {t.stripe_payment_intent_id && <span>PI: <span className="font-mono">{t.stripe_payment_intent_id}</span></span>}
                                    {t.stripe_charge_id && <span>Charge: <span className="font-mono">{t.stripe_charge_id}</span></span>}
                                    {t.stripe_application_fee_id && <span>App fee: <span className="font-mono">{t.stripe_application_fee_id}</span></span>}
                                    {t.session_id && <span>Session: <span className="font-mono">{t.session_id.slice(-18)}</span></span>}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const FeeLine = ({ label, value, accent }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-muted-foreground">{label}</span>
    <span className={`font-mono ${accent ? "text-[#FFCC00] font-semibold" : "text-foreground"}`}>{value}</span>
  </div>
);

const Cap = ({ label, enabled, testId }) => (  <span
    className={`uppercase tracking-wider px-2 py-1 rounded-md border ${
      enabled
        ? "border-emerald-500/30 text-emerald-500/90 bg-emerald-500/5"
        : "border-border text-muted-foreground bg-muted/20"
    }`}
    data-testid={testId}
  >
    {enabled ? "✓" : "·"} {label}
  </span>
);

const PeriodCard = ({ title, volume, fees, count, accent }) => (
  <div className={`rounded-xl border p-3 space-y-2 ${accent ? "border-[#FFCC00]/30 bg-[#FFCC00]/5" : "border-border bg-muted/20"}`}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{title}</p>
    <div className="space-y-1">
      <div className="flex items-center gap-1 text-foreground font-semibold">
        <TrendingUp className="w-3 h-3 text-cyan-400" />
        <span>{fmtMoney(volume)}</span>
      </div>
      <div className="flex items-center gap-1 text-[#FFCC00] text-sm">
        <DollarSign className="w-3 h-3" />
        <span>{fmtMoney(fees)} fees</span>
      </div>
      <p className="text-[10px] text-muted-foreground">{count || 0} txns</p>
    </div>
  </div>
);

export default AdminStripeConnectDetailModal;
